import { useQueryClient } from '@tanstack/react-query';
import { Drawer, Form, message } from 'antd';
import React, { useContext, useMemo, useState } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/shallow';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { useSaveExperienceConfiguration } from 'app/hooks/data/core/extranet/experience/useExperience';
import { CORE_QUERY_KEYS as HOTEL_CORE_QUERY_KEYS } from 'app/hooks/data/core/extranet/hotel';
import { CORE_QUERY_KEYS as INVENTORY_CORE_QUERY_KEYS } from 'app/hooks/data/core/extranet/inventory';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import type { DayOfWeek } from 'app/typings/core/common/enum/dayOfWeek';
import { DAY_OF_WEEK } from 'app/typings/core/common/enum/dayOfWeek';
import type { StockConfigurationType } from 'app/typings/core/common/openingConfiguration/stockConfigurationType';
import { formatDateWithoutHour } from 'app/utils/dates';

import { inventoryStore } from '../../../../InventoryStore';
import { getMustHavePackNames } from '../_utils/getMustHavePackNames';

import styles from './ExperienceConfigurationDrawer.module.scss';
import { ExperienceConfigurationContent } from './_components/ExperienceConfigurationContent';
import { ExperienceConfigurationFooter } from './_components/ExperienceConfigurationFooter/ExperienceConfigurationFooter';
import { MustHavePacksConfirmationModal } from './_components/MustHavePacksConfirmationModal/MustHavePacksConfirmationModal';

type ExperienceConfigurationFormValues = {
  openingConfiguration: {
    stockConfiguration: StockConfigurationType;
  };
  closedDaysBeforeCheckIn?: number | null;
  lastMinuteEnabled?: boolean;
};

export const ExperienceConfigurationDrawer = () => {
  const [form] = Form.useForm();
  const { hotelId } = useContext(HotelContext);
  const { getExperience, packsList } = useContext(ModernHotelContext);

  const queryClient = useQueryClient();

  const { experienceStockConfig, setExperienceStockConfig, timePeriod } =
    useStore(
      inventoryStore,
      useShallow((state) => ({
        experienceStockConfig: state.experienceStockConfig,
        setExperienceStockConfig: state.setExperienceStockConfig,
        timePeriod: state.timePeriod,
      }))
    );

  const { mutateAsync: saveExperienceConfiguration, isPending } =
    useSaveExperienceConfiguration();

  const experience = getExperience(experienceStockConfig.expId);

  const [pendingValues, setPendingValues] =
    useState<ExperienceConfigurationFormValues | null>(null);

  const mustHavePackNames = useMemo(
    () => getMustHavePackNames(experience, packsList),
    [experience, packsList]
  );

  const shouldConfirmBeforeSave = mustHavePackNames.length > 0;

  const handleClose = () => {
    setExperienceStockConfig(undefined, false);
  };

  const handleDrawerOpenChange = (isOpen: boolean) => {
    if (isOpen && experienceStockConfig.expId) {
      form.resetFields();
    }
  };

  const handleOnFinish = (values: ExperienceConfigurationFormValues) => {
    if (!experience?.id || !experience?.hotelId) {
      return;
    }

    if (shouldConfirmBeforeSave) {
      setPendingValues(values);

      return;
    }

    void submitConfiguration(values);
  };

  const handleConfirmMustHave = () => {
    if (!pendingValues) {
      return;
    }

    void submitConfiguration(pendingValues);
  };

  const handleCancelMustHave = () => {
    setPendingValues(null);
  };

  const submitConfiguration = async (
    values: ExperienceConfigurationFormValues
  ) => {
    if (!experience?.id || !experience?.hotelId) {
      return;
    }

    const normalizedValues = { ...values };
    const stockType =
      normalizedValues.openingConfiguration?.stockConfiguration?.type;

    const lastMinuteEnabled =
      normalizedValues.lastMinuteEnabled ??
      (experience?.closedDaysBeforeCheckIn !== null &&
        experience?.closedDaysBeforeCheckIn !== undefined);
    const closedDaysBeforeCheckIn =
      normalizedValues.closedDaysBeforeCheckIn ??
      experience?.closedDaysBeforeCheckIn;

    if (
      stockType === OPENING_CONFIGURATION_TYPE.CONSTANT &&
      experience?.isAlwaysIncludedWithNight
    ) {
      normalizedValues.openingConfiguration.stockConfiguration = {
        type: OPENING_CONFIGURATION_TYPE.CONSTANT,
        configuration: {
          stock: null,
        },
      };
    }

    if (stockType === OPENING_CONFIGURATION_TYPE.DAILY) {
      const stockConfig =
        normalizedValues.openingConfiguration?.stockConfiguration;

      if (
        stockConfig &&
        stockConfig.type === OPENING_CONFIGURATION_TYPE.DAILY
      ) {
        const days = { ...stockConfig.days };

        Object.values(DAY_OF_WEEK).forEach((day) => {
          const dayValue = days[day] as {
            stock?: number | null;
            isDisabled?: boolean;
            isToggled?: boolean;
          };

          days[day] = {
            isClosed: !dayValue?.isToggled,
            stock: !dayValue?.isToggled ? 0 : (dayValue?.stock ?? null),
          };
        });

        normalizedValues.openingConfiguration.stockConfiguration = {
          ...stockConfig,
          days,
        };
      }
    }

    await saveExperienceConfiguration({
      hotelId: experience.hotelId,
      experienceId: experience.id,
      openingConfiguration: normalizedValues.openingConfiguration,
      closedDaysBeforeCheckIn: lastMinuteEnabled
        ? closedDaysBeforeCheckIn
        : null,
    })
      .then(() => {
        message.success('Activity configuration saved successfully');

        queryClient.invalidateQueries({
          queryKey: [HOTEL_CORE_QUERY_KEYS.hotelExperiences, { id: hotelId }],
        });

        queryClient.invalidateQueries({
          queryKey: [
            INVENTORY_CORE_QUERY_KEYS.inventory,
            {
              hotelId,
              startDate: formatDateWithoutHour(timePeriod.startDate),
              endDate: formatDateWithoutHour(timePeriod.endDate),
            },
          ],
        });
        setPendingValues(null);
        handleClose();
      })
      .catch(() => {
        setPendingValues(null);
        message.error('Failed to save activity configuration');
      });
  };

  const getInitialValues = () => {
    if (!experience) {
      return {};
    }

    const stockConfiguration =
      experience.openingConfiguration?.stockConfiguration;
    const priceConfiguration =
      experience.openingConfiguration?.priceConfiguration;

    if (stockConfiguration?.type === OPENING_CONFIGURATION_TYPE.DAILY) {
      const daysWithToggled = Object.entries(stockConfiguration.days).reduce(
        (acc, [day, dayConfig]) => {
          const isPriceEmpty = (() => {
            if (!priceConfiguration) {
              return true;
            }

            if (experience.isAlwaysIncludedWithNight) {
              if (
                priceConfiguration.type === OPENING_CONFIGURATION_TYPE.CONSTANT
              ) {
                return (
                  priceConfiguration.configuration.daytimePrice === null ||
                  priceConfiguration.configuration.daytimePrice === undefined
                );
              }

              return (
                priceConfiguration.days[day as DayOfWeek]?.daytimePrice ===
                  null ||
                priceConfiguration.days[day as DayOfWeek]?.daytimePrice ===
                  undefined
              );
            }

            if (
              priceConfiguration.type === OPENING_CONFIGURATION_TYPE.CONSTANT
            ) {
              return (
                priceConfiguration.configuration.price === null ||
                priceConfiguration.configuration.price === undefined
              );
            }

            return (
              priceConfiguration.days[day as DayOfWeek]?.price === null ||
              priceConfiguration.days[day as DayOfWeek]?.price === undefined
            );
          })();

          const isClosedByHotel = dayConfig.isClosed && dayConfig.stock === 0;

          return {
            ...acc,
            [day]: {
              ...dayConfig,
              isToggled: !isClosedByHotel,
              isDisabled: isPriceEmpty,
            },
          };
        },
        {}
      );

      return {
        openingConfiguration: {
          stockConfiguration: {
            type: stockConfiguration.type,
            days: daysWithToggled,
          },
        },
        closedDaysBeforeCheckIn: experience.closedDaysBeforeCheckIn,
        lastMinuteEnabled:
          experience.closedDaysBeforeCheckIn !== null &&
          experience.closedDaysBeforeCheckIn !== undefined,
      };
    }

    return {
      openingConfiguration: {
        ...experience?.openingConfiguration,
        stockConfiguration: {
          ...experience?.openingConfiguration?.stockConfiguration,
          configuration: {
            stock:
              experience?.openingConfiguration?.stockConfiguration?.type ===
              OPENING_CONFIGURATION_TYPE.CONSTANT
                ? (experience.openingConfiguration.stockConfiguration
                    .configuration?.stock ?? null)
                : undefined,
          },
        },
      },
      closedDaysBeforeCheckIn: experience?.closedDaysBeforeCheckIn,
      lastMinuteEnabled:
        experience?.closedDaysBeforeCheckIn !== null &&
        experience?.closedDaysBeforeCheckIn !== undefined,
    };
  };

  return (
    <Drawer
      title={<div className={styles.experienceHeader}>Configure stock</div>}
      className={styles.experienceDrawerConfig}
      width="510px"
      closable
      open={!!experienceStockConfig?.isOpen}
      bodyStyle={{ padding: 0 }}
      onClose={handleClose}
      afterOpenChange={handleDrawerOpenChange}
      destroyOnClose
      footerStyle={{ padding: 0 }}
      footer={
        <ExperienceConfigurationFooter
          handleClose={handleClose}
          handleSave={form.submit}
          isLoading={isPending}
        />
      }
    >
      <Form
        key={experienceStockConfig?.expId}
        name={`experience-configuration-${experienceStockConfig?.expId}`}
        form={form}
        onFinish={handleOnFinish}
        initialValues={getInitialValues()}
        preserve={false}
      >
        <ExperienceConfigurationContent experience={experience} />
      </Form>
      <MustHavePacksConfirmationModal
        isOpened={!!pendingValues}
        packNames={mustHavePackNames}
        isLoading={isPending}
        handleCancel={handleCancelMustHave}
        handleSaveChange={handleConfirmMustHave}
      />
    </Drawer>
  );
};
